import React, { useState, useRef } from 'react';
import { ArrowLeft, Camera, FileText, Calendar, Building2, Package, IndianRupee, Hash, Upload, CheckCircle, Plus, Trash2, X } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { PRODUCTS, formatINR } from '@/data/mockData';

export default function SalesInvoiceUploadScreen() {
  const { goBack, navigate, addSalesInvoice } = useApp();
  const fileRef = useRef(null);
  
  const [phase, setPhase] = useState('form'); // form | uploading | success
  const [formData, setFormData] = useState({
    invoiceNo: '',
    invoiceDate: '',
    hospitalName: '',
    totalAmount: '',
    remarks: '',
  });
  const [lineItems, setLineItems] = useState([
    { productId: '', productName: '', quantity: '', amount: '' }
  ]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleLineItemChange = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    
    // If product selected, auto-fill product name
    if (field === 'productId' && value) {
      const product = PRODUCTS.find(p => p.id === parseInt(value));
      if (product) {
        updated[index].productName = product.name;
      }
    }
    
    setLineItems(updated);
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { productId: '', productName: '', quantity: '', amount: '' }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const calculateTotal = () => {
    return lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.invoiceNo.trim()) newErrors.invoiceNo = 'Invoice number is required';
    if (!formData.invoiceDate) newErrors.invoiceDate = 'Invoice date is required';
    if (!formData.hospitalName.trim()) newErrors.hospitalName = 'Hospital name is required';
    
    const hasValidItems = lineItems.some(item => item.productName && item.quantity && item.amount);
    if (!hasValidItems) newErrors.lineItems = 'At least one product with quantity and amount is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    
    setPhase('uploading');
    
    // Calculate total from line items or use manual entry
    const calculatedTotal = calculateTotal();
    const finalTotal = formData.totalAmount ? parseFloat(formData.totalAmount) : calculatedTotal;
    
    // Simulate upload delay
    setTimeout(() => {
      // Add invoice to context
      const newInvoice = {
        invoiceNo: formData.invoiceNo,
        date: formData.invoiceDate,
        hospitalName: formData.hospitalName,
        amount: finalTotal,
        lineItems: lineItems.filter(item => item.productName && item.amount),
        remarks: formData.remarks,
        fileName: selectedFile?.name || null,
        status: 'UNDER_REVIEW',
      };
      
      addSalesInvoice(newInvoice);
      setPhase('success');
    }, 2200);
  };

  /* ── Uploading state ── */
  if (phase === 'uploading') {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-8">
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
          style={{ background: '#EFF6FF', animation: 'pulse 1.4s ease-in-out infinite' }}
        >
          <Upload size={36} color="#2563EB" />
        </div>
        <div className="font-black text-gray-800 text-lg mb-2">Uploading Invoice...</div>
        <div className="text-gray-400 text-sm">Processing your sales invoice</div>
        <div className="mt-8 w-48 h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#2563EB] rounded-full"
            style={{ animation: 'progress-fill 2.2s ease-in-out forwards' }}
          />
        </div>
        <style>{`
          @keyframes progress-fill {
            from { width: 0% }
            to { width: 100% }
          }
        `}</style>
      </div>
    );
  }

  /* ── Success state ── */
  if (phase === 'success') {
    return (
      <div
        data-testid="sales-invoice-upload-success"
        className="min-h-screen bg-white flex flex-col items-center justify-center px-8"
      >
        <div
          className="w-24 h-24 rounded-full flex items-center justify-center mb-6"
          style={{ background: '#F0FDF4' }}
        >
          <CheckCircle size={52} color="#22C55E" strokeWidth={1.8} />
        </div>
        <h1 className="font-black text-2xl text-gray-900 text-center leading-snug mb-3">
          Sales Invoice<br />Uploaded Successfully!
        </h1>
        <p className="text-gray-400 text-sm text-center mb-4">
          Invoice #{formData.invoiceNo} has been submitted for review
        </p>
        <div className="bg-blue-50 rounded-2xl p-4 w-full mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-blue-600 text-xs font-semibold">Hospital</span>
            <span className="text-blue-900 text-sm font-bold">{formData.hospitalName}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-blue-600 text-xs font-semibold">Total Amount</span>
            <span className="text-blue-900 text-lg font-black">₹{formatINR(formData.totalAmount || calculateTotal())}</span>
          </div>
        </div>
        <button
          data-testid="success-view-invoices-btn"
          onClick={() => navigate('INVOICES')}
          className="w-full text-white font-black py-4 rounded-2xl tap-scale text-base"
          style={{ background: 'linear-gradient(to right, #2563EB, #1D4ED8)' }}
        >
          View All Sales Invoices
        </button>
        <button
          onClick={() => navigate('HOME')}
          className="mt-3 w-full text-gray-500 font-semibold py-3 tap-scale text-sm"
        >
          Back to Home
        </button>
      </div>
    );
  }

  /* ── Form state ── */
  return (
    <div className="min-h-screen bg-[#F8F9FA] pb-8">
      {/* Header */}
      <div className="bg-[#2563EB] px-4 pt-5 pb-5">
        <div className="flex items-center gap-3">
          <button
            data-testid="sales-invoice-back-btn"
            onClick={goBack}
            className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center tap-scale"
          >
            <ArrowLeft size={18} color="white" />
          </button>
          <div>
            <h1 className="text-white font-bold text-lg">Upload Sales Invoice</h1>
            <p className="text-white/70 text-xs">Record your hospital sales</p>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4 flex flex-col gap-4">
        {/* Invoice Details Card */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <div className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
            <FileText size={14} />
            Invoice Details
          </div>
          
          {/* Invoice Number */}
          <div className="mb-4">
            <label className="text-[10px] text-slate-400 uppercase font-bold tracking-wider block mb-1.5 flex items-center gap-1">
              <Hash size={10} />
              Invoice Number *
            </label>
            <input
              data-testid="invoice-number-input"
              type="text"
              value={formData.invoiceNo}
              onChange={(e) => handleInputChange('invoiceNo', e.target.value)}
              placeholder="e.g., INV-2026-001"
              className={`w-full bg-slate-50 border ${errors.invoiceNo ? 'border-red-300' : 'border-slate-200'} rounded-xl px-3 py-3 text-sm font-medium text-slate-700 focus:border-[#2563EB] focus:outline-none focus:ring-2 focus:ring-blue-100 transition-all`}
            />
            {errors.invoiceNo && <p className="text-red-500 text-xs mt-1">{errors.invoiceNo}</p>}
          </div>

          {/* Invoice Date */}
          <div className="mb-4">
            <label className="text-[10px] text-slate-400 uppercase font-bold tracking-wider block mb-1.5 flex items-center gap-1">
              <Calendar size={10} />
              Invoice Date *
            </label>
            <input
              data-testid="invoice-date-input"
              type="date"
              value={formData.invoiceDate}
              onChange={(e) => handleInputChange('invoiceDate', e.target.value)}
              className={`w-full bg-slate-50 border ${errors.invoiceDate ? 'border-red-300' : 'border-slate-200'} rounded-xl px-3 py-3 text-sm font-medium text-slate-700 focus:border-[#2563EB] focus:outline-none focus:ring-2 focus:ring-blue-100 transition-all`}
            />
            {errors.invoiceDate && <p className="text-red-500 text-xs mt-1">{errors.invoiceDate}</p>}
          </div>

          {/* Hospital Name */}
          <div className="mb-4">
            <label className="text-[10px] text-slate-400 uppercase font-bold tracking-wider block mb-1.5 flex items-center gap-1">
              <Building2 size={10} />
              Hospital / Customer Name *
            </label>
            <input
              data-testid="hospital-name-input"
              type="text"
              value={formData.hospitalName}
              onChange={(e) => handleInputChange('hospitalName', e.target.value)}
              placeholder="e.g., City Women's Hospital"
              className={`w-full bg-slate-50 border ${errors.hospitalName ? 'border-red-300' : 'border-slate-200'} rounded-xl px-3 py-3 text-sm font-medium text-slate-700 focus:border-[#2563EB] focus:outline-none focus:ring-2 focus:ring-blue-100 transition-all`}
            />
            {errors.hospitalName && <p className="text-red-500 text-xs mt-1">{errors.hospitalName}</p>}
          </div>
        </div>

        {/* Product Line Items Card */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <div className="flex justify-between items-center mb-4">
            <div className="text-xs font-black text-slate-500 uppercase tracking-widest flex items-center gap-2">
              <Package size={14} />
              Products Sold
            </div>
            <button
              data-testid="add-line-item-btn"
              onClick={addLineItem}
              className="flex items-center gap-1 text-xs font-bold text-[#2563EB] tap-scale"
            >
              <Plus size={14} />
              Add Item
            </button>
          </div>
          
          {errors.lineItems && <p className="text-red-500 text-xs mb-3">{errors.lineItems}</p>}

          {lineItems.map((item, index) => (
            <div key={index} className="mb-4 pb-4 border-b border-slate-100 last:border-0 last:mb-0 last:pb-0">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-bold text-slate-400">Item {index + 1}</span>
                {lineItems.length > 1 && (
                  <button
                    data-testid={`remove-item-${index}`}
                    onClick={() => removeLineItem(index)}
                    className="text-red-400 tap-scale"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
              
              {/* Product Selection */}
              <div className="mb-3">
                <select
                  data-testid={`product-select-${index}`}
                  value={item.productId}
                  onChange={(e) => handleLineItemChange(index, 'productId', e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-700 focus:border-[#2563EB] focus:outline-none"
                >
                  <option value="">Select Product</option>
                  {PRODUCTS.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name} - {product.pack}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Or enter custom product name */}
              {!item.productId && (
                <div className="mb-3">
                  <input
                    data-testid={`product-name-${index}`}
                    type="text"
                    value={item.productName}
                    onChange={(e) => handleLineItemChange(index, 'productName', e.target.value)}
                    placeholder="Or enter product name"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-700 focus:border-[#2563EB] focus:outline-none"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-slate-400 uppercase font-bold tracking-wider block mb-1">Quantity</label>
                  <input
                    data-testid={`quantity-input-${index}`}
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                    placeholder="Qty"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-700 focus:border-[#2563EB] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-slate-400 uppercase font-bold tracking-wider block mb-1">Amount (₹)</label>
                  <input
                    data-testid={`amount-input-${index}`}
                    type="number"
                    value={item.amount}
                    onChange={(e) => handleLineItemChange(index, 'amount', e.target.value)}
                    placeholder="Amount"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-700 focus:border-[#2563EB] focus:outline-none"
                  />
                </div>
              </div>
            </div>
          ))}

          {/* Calculated Total */}
          {calculateTotal() > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-200 flex justify-between items-center">
              <span className="text-sm font-bold text-slate-600">Calculated Total</span>
              <span className="text-lg font-black text-[#1E2744]">₹{formatINR(calculateTotal())}</span>
            </div>
          )}
        </div>

        {/* Total Amount Override (Optional) */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <div className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2">
            <IndianRupee size={14} />
            Total Sale Amount (Optional Override)
          </div>
          <input
            data-testid="total-amount-input"
            type="number"
            value={formData.totalAmount}
            onChange={(e) => handleInputChange('totalAmount', e.target.value)}
            placeholder="Enter total if different from calculated"
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-3 text-sm font-medium text-slate-700 focus:border-[#2563EB] focus:outline-none focus:ring-2 focus:ring-blue-100 transition-all"
          />
          <p className="text-xs text-slate-400 mt-2">Leave empty to use calculated total from line items</p>
        </div>

        {/* File Upload Card */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <div className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2">
            <Upload size={14} />
            Upload Invoice Image/PDF (Optional)
          </div>
          
          <div
            className="border-2 border-dashed border-slate-200 rounded-xl p-5 text-center bg-slate-50 cursor-pointer tap-scale"
            onClick={() => fileRef.current?.click()}
          >
            {selectedFile ? (
              <div className="flex items-center justify-center gap-2">
                <FileText size={20} color="#2563EB" />
                <span className="text-sm font-medium text-slate-700">{selectedFile.name}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                  }}
                  className="text-slate-400 hover:text-red-500"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <>
                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Camera size={24} color="#2563EB" />
                </div>
                <div className="text-slate-600 font-semibold text-sm">Tap to upload invoice</div>
                <div className="text-slate-400 text-xs mt-1">Supports images and PDF files</div>
              </>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            accept="image/*,.pdf"
            onChange={handleFileSelect}
          />
        </div>

        {/* Remarks */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <div className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3">
            Remarks (Optional)
          </div>
          <textarea
            data-testid="remarks-input"
            value={formData.remarks}
            onChange={(e) => handleInputChange('remarks', e.target.value)}
            placeholder="Any additional notes..."
            rows={3}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-3 text-sm font-medium text-slate-700 focus:border-[#2563EB] focus:outline-none focus:ring-2 focus:ring-blue-100 transition-all resize-none"
          />
        </div>

        {/* Submit Button */}
        <button
          data-testid="submit-sales-invoice-btn"
          onClick={handleSubmit}
          className="w-full bg-[#2563EB] text-white rounded-2xl py-4 font-bold text-base tracking-wide tap-scale shadow-lg"
        >
          Submit Sales Invoice
        </button>
      </div>
    </div>
  );
}
